import React, { useState, useEffect, useRef, useMemo, useReducer } from 'react';
import axios from 'axios';
import { useHistory, useLocation } from "react-router-dom";
import { fetchUser } from "../services/authService";
import { areArraysEqual, getCurrentResearcherUuid, researcherFullName } from '../utils/index.jsx';
import { SIGN_IN_ROUTE } from '../routes/urls';
import { FormControl, FormControlLabel, FormLabel, Grid, InputLabel, MenuItem, Radio, RadioGroup, Select, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { FieldWrapper } from '../components/general/mini_components';
import { useDispatch, useSelector } from 'react-redux';
import { getAgendasInvestigationAction, getDepartmentsInvestigationAction } from '../redux/actions/hospitalActions';
import { Color } from '@mui/lab';
import { IDepartment, IInsurance, IPatient, IResearcher, IUnit, IBox, DepartmentType } from '../constants/types';
import { INITIAL_SELECT } from '../components/general/SmartFields';
import { IRequest, IService, IServiceInvestigation, RequestStatus } from '../pages/hospital/Request/types';
import { fetchProfileInfoAction } from '../redux/actions/profileActions';
import Loader from '../components/Loader';
import { Done, EventBusy, MoneyOff, Paid } from "@mui/icons-material";
import { getServiceGeneralService } from '../services/agenda';
import { isArray, isObject, set } from 'lodash';
import { DocumentStatus } from '../pages/hospital/Billing/types';
import { ColourChip } from '../components/general/mini_components-ts';
import { documentStatusToColor } from '../utils/bill';
import { fetchSingleSubmissionsPatientInvestigationAction, fetchSubmissionsPatientInvestigationAction } from '../redux/actions/submissionsPatientActions';
import { deletePatientAction, fetchPatientsAction } from '../redux/actions/patientsActions';
import { getBoxesService } from '../services/agenda';

export interface SnackbarType{
    show: boolean;
    message?: string | undefined;
    severity?: Color | undefined;
    
}

export function useSnackBarState(){
    const [showSnackbar, setShowSnackbar] = useState<SnackbarType>({show : false});

    function handleCloseSnackbar(){
        setShowSnackbar({show:false});
    }

    return [showSnackbar, setShowSnackbar, handleCloseSnackbar] as const;
}

export function useRouter(initValue:any){
    const history = useHistory();
    return {
        pathname : initValue ? initValue : history.location.pathname,

    }
}

export function useLoadingMessage(){
    const investigations = useSelector((state:any) => state.investigations);
    const loading = useSelector((state:any) => state.investigations.loading);

    let message;

    switch(loading){
        case 1:
            message = "general.loading.investigations";
            break;
        case 2:
            message = "general.loading.decrypting";
            break;
        default:
            message = "general.loading.loading";
    }
   
    return [loading, (
        <>
            <Loader infoString={<Translate id={`${message}`} />} />
        </>
    )]
}

export function useProfileInfo(){
    const investigations = useSelector((state:any) => state.investigations);
    const profile = useSelector((state:any) => state.profile.info);
    const loadingProfile = useSelector((state:any) => state.investigations.loading | state.profile.loading);
    
    const dispatch = useDispatch();

    useEffect(() => {
        
        if(investigations.currentInvestigation){
            if(!profile && !loadingProfile){
                dispatch(fetchProfileInfoAction(investigations.currentInvestigation.uuid));
            }
        }
    }, [ investigations]);

    return { profile, loadingProfile }
}



export function useServiceGeneral(serviceType:number){
    const [servicesGeneral, setServicesGeneral] = useState<IService[]>([]);
    const [loadingServicesGeneral, setLoadingServicesGeneral] = useState(false);
    useEffect(() => {
        
            if (!servicesGeneral) {
                setLoadingServicesGeneral(true);
                getServiceGeneralService(serviceType)
                    .then(response => {
                        setServicesGeneral(response.services);
                        setLoadingServicesGeneral(true);
                    })
                    .catch(err => {
                        setLoadingServicesGeneral(true);
                    })
            } 
    }, []);

    return { servicesGeneral, loadingServicesGeneral }
}

// ... existing code ...

export function useServiceInvestigation(serviceType: number) {
    const investigations = useSelector((state: any) => state.investigations);
    const [services, setServices] = useState<IServiceInvestigation[]>([]);
    const [loadingServices, setLoadingServices] = useState(false);

    useEffect(() => {
        async function fetchServices() {
            if (investigations.currentInvestigation?.uuid) {
                setLoadingServices(true);
                try {
                    const response = await axios.get(
                        `${import.meta.env.VITE_APP_API_URL}/hospital/${investigations.currentInvestigation.uuid}/services/${serviceType}`,
                        { 
                            headers: { "Authorization": localStorage.getItem("jwt") }
                        }
                    );
                    
                    if (response.status === 200) {
                        setServices(response.data.servicesInvestigation);
                    }
                } catch (error) {
                    console.error('Error fetching services:', error);
                } finally {
                    setLoadingServices(false);
                }
            }
        }

        fetchServices();
    }, [investigations.currentInvestigation, serviceType]);

    return { services, loadingServices };
}


export function usePatientSubmission(idSubmission:number, uuidPatient:string){
    const investigations = useSelector((state:any) => state.investigations);
    const submissions = useSelector((state:any) => state.patientsSubmissions.data);
    const loadingSubmissions = useSelector((state:any) => state.patientsSubmissions.loading);
    const [submission, setSubmission] = useState<any>(null);
    const dispatch = useDispatch();

    useEffect(() => {
        async function getSubmission() {
            await dispatch(
                fetchSingleSubmissionsPatientInvestigationAction(investigations.currentInvestigation.uuid, idSubmission, uuidPatient)
            );
        }
        if(investigations.data && investigations.currentInvestigation && idSubmission && !submission){
            const foundSubmission = findSubmission(idSubmission);
            if(foundSubmission === undefined){
                getSubmission();   
            }
            else{
                setSubmission(foundSubmission);
            }
        }    
    }, [idSubmission, submission]);   
    
    function findSubmission(idSubmission:number){
        if(submissions && idSubmission){
            const findSubmission = Object.values(submissions[uuidPatient]).flatMap((submissionsSurvey:any) => {
                return submissionsSurvey.submissions;
            }).find((subi:any) => subi.id === idSubmission);
        
            return findSubmission;
        }
    }

    useEffect(() => {
        const foundSubmission = findSubmission(idSubmission);
        if(foundSubmission !== undefined){
            setSubmission(foundSubmission);
        }
    }, [submissions])

    return [submission, loadingSubmissions];
}

// Export new coverage-based insurance hooks from separate file
export { usePatientInsurance, useInsurancesList, useCoveragesList, useCoverage } from './usePatientInsurance';

export function useDepartments(uuidResearchersDepartmentsOnly:string = ""){
    const investigations= useSelector((state:any) => state.investigations);
    const departments:IDepartment[] | null = useSelector((state:{hospital : {data: {departments : IDepartment[]}}}) => state.hospital.data.departments ? state.hospital.data.departments : null);
    const researchers:IResearcher[] = useSelector((state:any) => state.hospital.data.researchers ? state.hospital.data.researchers : []);
    const loadingDepartments:boolean = useSelector((state:any) => state.hospital.loading || state.investigations.loading);
    const [filteredDepartments, setFilteredDepartments] = useState<IDepartment[]>([]);

    // Only select the specific investigation properties we need
    const investigationUuid = useSelector((state:any) => 
        state.investigations.data && state.investigations.currentInvestigation 
            ? state.investigations.currentInvestigation.uuid 
            : null
    );

    const dispatch = useDispatch();

    useEffect(()=>{
        async function getDepartments(uuidInvestigation:string){
            await dispatch(
                getDepartmentsInvestigationAction(uuidInvestigation)
            ); 
        }
        if(investigationUuid && departments === null){
            getDepartments(investigationUuid)
        }
    }, [investigationUuid, departments, dispatch])

    useEffect(() => {
        if(departments !== null){
            let tempFiltered = [...departments];
            if(uuidResearchersDepartmentsOnly !== ""){
                const currentResearcher = researchers.find((researcher:any) => researcher.uuid === uuidResearchersDepartmentsOnly);
                if(currentResearcher){
                    const tempDepartments:{[uuidDepartment :string]: IDepartment} = {};
                    currentResearcher.units.forEach((unit:IUnit) => {
                        const department = departments.find((department:IDepartment) => department.uuid === unit.department.uuid);
                        tempDepartments[unit.department.uuid as string] = department;
                    })
                    tempFiltered = Object.values(tempDepartments);
                }
            }
            setFilteredDepartments(tempFiltered);
        }
    }, [uuidResearchersDepartmentsOnly, departments, researchers])

    return { departments: filteredDepartments, researchers, investigations, loadingDepartments}
}


export const useResearcher = (uuid: string) => {
    const { researchers, loadingDepartments : loading } = useDepartments();
    const [researcherFound, setResearcherFound] = useState<IResearcher | null>(null);

    useEffect(() => {
        const foundResearcher = researchers.find((researcher) => researcher.uuid === uuid);
        if(foundResearcher){
            setResearcherFound(foundResearcher)
        }
        else{
            setResearcherFound(null);
        }
    }, [researchers])
  
    return { 
        researcher : researcherFound, 
        loading,
        error: !researcherFound && !loading ? new Error('Researcher not found') : null
    };
  };

export function useResearchersByDepartmentType(departmentType: DepartmentType) {
    const investigations = useSelector((state: any) => state.investigations);
    const departments: IDepartment[] | null = useSelector((state: any) => 
        state.hospital.data.departments ? state.hospital.data.departments : null
    );
    const researchersAll:IResearcher[] = useSelector((state:any) => state.hospital.data.researchers ? state.hospital.data.researchers : []);
    const loadingDepartments = useSelector((state: any) => 
        state.hospital.loading || state.investigations.loading
    );
    const [researchers, setResearchers] = useState<IResearcher[]>([]);

    const dispatch = useDispatch();

    // Fetch departments if needed
    useEffect(() => {
        async function getDepartments(uuidInvestigation: string) {
            await dispatch(getDepartmentsInvestigationAction(uuidInvestigation));
        }
        if (investigations.data && investigations.currentInvestigation && departments === null) {
            getDepartments(investigations.currentInvestigation.uuid);
        }
    }, [investigations, departments, dispatch]);

    // Process departments to get unique researchers
    useEffect(() => {
        if (departments) {
            // Get all researchers from departments of the specified type
            const researchersMap = new Map<string, IResearcher>();
            
            departments
                .filter(department => department.type === departmentType)
                .forEach(department => {
                    department.units.forEach(unit => {
                        researchersAll.filter((researcher) => {
                            if(researcher.units.find((unitResearcher) => unitResearcher.uuid === unit.uuid)){
                                researchersMap.set(researcher.uuid, researcher);
                            }
                        })
                    });
                });

            setResearchers(Array.from(researchersMap.values()));
        }
    }, [departments, departmentType]);

    return {
        researchers,
        loadingDepartments,
        isEmpty: researchers.length === 0
    };
}   


export function useRequest(idRequest: number) {
    const currentInvestigationUuid = useSelector((state: any) => 
        state.investigations.currentInvestigation?.uuid
    );
    const hasInvestigationsData = useSelector((state: any) => 
        Boolean(state.investigations.data)
    );
    const [loadingRequest, setLoadingRequest] = useState(false);
    const [request, setRequest] = useState<IRequest | null>(null);

    useEffect(() => {
        const fetchRequest = async () => {
            setLoadingRequest(true);
            
            const response = await axios(`${import.meta.env.VITE_APP_API_URL}/hospital/${currentInvestigationUuid}/request/${idRequest}`, { 
                headers: {"Authorization" : localStorage.getItem("jwt")} 
            }).catch(err => {
                console.log('Catch', err); 
                return err;
            });
            
            if(response.status === 200){
                setRequest(response.data.request);
            }
            setLoadingRequest(false);
        }
        
        if(hasInvestigationsData && currentInvestigationUuid){
            fetchRequest();
        }
        
    }, [idRequest, currentInvestigationUuid, hasInvestigationsData])

    return { request, loadingRequest}
}

export function useSelectSmartField(initialState:any, label:string, type:string, errorState:boolean, setAddingSmartField:(adding:boolean) => void){
    //Hay que devolver undefined para que el select no ponga el placeholder arriba
    const [addSmartField, setAddSmartField] = useState(initialState && initialState.addSmartField ? initialState.addSmartField : !INITIAL_SELECT.includes(type) ? true : undefined);

    function selectChanged(event:React.ChangeEvent<{
        name?: string | undefined;
        value: unknown;
    }>){
        console.log(event);
        setAddSmartField(event.target.value);
    }
    function resetState(){
        setAddSmartField(!INITIAL_SELECT.includes(type));
        setAddingSmartField(true);
    }
    function renderSelect(){
        const optionsArray = [<MenuItem value={1}><Translate id="general.yes" /></MenuItem>, <MenuItem value={0}><Translate id="general.no" /></MenuItem>]
        
        return(
            <FieldWrapper>
                <FormControl variant="outlined" fullWidth margin="dense" error={errorState}>
                    <InputLabel id="show_treatment">{label}</InputLabel>
                    <Select
                        labelId="show_treatment"
                        id="show_treatment"
                        label={label}
                        value={addSmartField}
                        onChange={(event) => selectChanged(event)}
                    >
                    { optionsArray }
                    </Select>
                </FormControl>
            </FieldWrapper>
        )
    }
    return [addSmartField, renderSelect, resetState]
}


export function useSherwoodUser(){
    const history = useHistory();
    let location = useLocation();
    const initialLocation = location.pathname;
    const [isLoading, setIsLoading] = useState(true);
    const [isAuth, setIsAuth] = useState(true);
    const [error, setError] = useState(false);
    
    useEffect(() => {
     
        async function checkUser(){
            try{
                console.log("JWT", localStorage.getItem("jwt"));
            
                if(localStorage.getItem("jwt") !==""){
                    const isAuth = await fetchUser();
                    
                    if(isAuth){
                        setIsAuth(true);
                    }   
                    else{
                        setIsAuth(false);
                    } 
                }
                else{
                    setIsAuth(false);
                }
                setIsLoading(false);
            }
            catch(error){
                console.log(error);
                setIsLoading(false);
                setIsAuth(false);
                setError(true);
            }
        }
        
        checkUser();
        
    }, [history])
    
    if(!isAuth){        
        history.push({
            pathname: SIGN_IN_ROUTE,
            state: { 
                from: history.location.pathname
            }
        })
    }
    

    return {isLoading, isAuth, error}
}

/**
 * A custom useEffect hook that only triggers on updates, not on initial mount
 * Idea stolen from: https://stackoverflow.com/a/55075818/1526448
 * @param {Function} effect
 * @param {Array<any>} dependencies
 */
 export function useUpdateEffect(effect:() => void, dependencies:any = []) {
    const didMount = useRef(false);

    useEffect(() => {
        if (didMount.current) effect();
        else didMount.current = true;
    }, dependencies);
  }

export function usePrevious(value:any, withInitial?:boolean){
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    if(ref.current === undefined && withInitial){
        if(isArray(value)){
            return [...value];
        }
        else if(isObject(value)){
            return {...value};
        }
        return value;
        
    }
    return ref.current as any;
}

export function useOffline(){
    const [offline, setOffline] = React.useState(!navigator.onLine);

    function updateOffline(){
        if(offline === navigator.onLine){
            setOffline(!navigator.onLine);
        }
    }
    useEffect(() => {
        async function subscribeToDB(){
            // const db = await openStore("workbox-background-sync");
            // var tx = db.transaction("requests", "readwrite");

            // tx.objectStore("requests").openCursor(0).onsuccess = function (event:any) {
            //     var cursor, object;

            //     cursor = event.target.result;
            //     object = cursor.value;
            //     object.value = 43;
            //     cursor.update(object).onsuccess = function (event:any) {
            //         db.transaction("requests").objectStore("requests").get(0).onsuccess = function (event:any) {
            //             console.log("Cursor update onsuccess event:");
            //             console.log(event.target.result);
            //         };
            //     };

            // };
        }
        window.addEventListener('online',  updateOffline);
        window.addEventListener('offline', updateOffline);
       
        subscribeToDB();
        
    }, [offline]);


    return offline;
}

export function useUnitSelector(units:IUnit[]){
    const [unitSelected, setUnitSelected] = React.useState<string | null>(null);
    const [errorUnit, setErrorUnit] = React.useState(false);

    useEffect(() => {
        if(units.length === 1){
            setUnitSelected(units[0].uuid as string);
        }
    }, [units])

    function markAsErrorUnit(){
        setErrorUnit(true);
    }

    function renderUnitSelector(){
        if(units.length === 1){
            return null;
        }
        else{
            const optionsArray = units.map((unit) => {
                return <MenuItem value={unit.uuid}>{unit.department.name} - {unit.name}</MenuItem>
            })
            return(
                <Grid item xs={12} style={{paddingTop:'0.5rem', paddingBottom:'0.5rem'}}>
                    <FormControl variant="outlined"  style={{minWidth: 220}} error={errorUnit} >
                    <InputLabel id="unit"><Translate id="pages.hospital.pharmacy.request.select_unit" /></InputLabel>
                        <Select 
                            labelId="unit"
                            id="unit"
                            label="unit"
                            value={unitSelected}
                            onChange={(event) => {
                                setErrorUnit(false);
                                setUnitSelected(event.target.value as string)}}
                        >
                        { optionsArray }
                        </Select>
                    </FormControl>
                </Grid>
            )
        }
        
    }
    return {unitSelected, renderUnitSelector, markAsErrorUnit}
}

export function useStatusDocument(status:DocumentStatus, editable:boolean = true){
    const [statusDocument, setStatusDocument] = React.useState<DocumentStatus>(status);

    const handleChangeStatus = (event: React.ChangeEvent<HTMLInputElement>) => {
        setStatusDocument(Number((event.target as HTMLInputElement).value));
    };

    function renderStatusDocument(){
        let renderStatusDocument = null;
        if(status === DocumentStatus.CLOSED){
            renderStatusDocument = <><Typography variant="body2" component='span' fontWeight='bold'><Translate id="hospital.billing.bill.status.label" /> : </Typography><ColourChip rgbcolor={documentStatusToColor(DocumentStatus.CLOSED)} label={<Translate id="hospital.billing.bill.closed" />}/></>
        }
        else{
            renderStatusDocument =  (
                <FormControl>
                <FormLabel id="demo-row-radio-buttons-group-label">
                        <Typography variant="body2" component='span' fontWeight='bold'><Translate id="hospital.billing.bill.status.label" /></Typography></FormLabel>
                <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={statusDocument}
                    onChange={handleChangeStatus}
                >
                    <FormControlLabel value={DocumentStatus.DRAFT} control={<Radio />} label={<Typography variant="body2" component='span'><Translate id="hospital.billing.bill.draft" /></Typography>} />
                    <FormControlLabel value={DocumentStatus.CLOSED} control={<Radio />} label={<Typography variant="body2" component='span' ><Translate id="hospital.billing.bill.closed" /></Typography>} />
                </RadioGroup>
                </FormControl>
            );
        }
        return renderStatusDocument;
    }
    
    return {statusDocument, renderStatusDocument}
}
interface OptionSelector{
    label:string,
    value:string
}
function useSelectLogic(optionsSelector:OptionSelector[], key:string, defaultValue?:string, canSelectNoOption:boolean = false, noReturnIfOnlyOne:boolean = false){
    const [optionSelected, setOptionSelected] = React.useState<string | null>(defaultValue ? defaultValue : "");
    const [error, setError] = React.useState(false);
    const [options, setOptions] = React.useState<OptionSelector[] | null>(null);

    useEffect(() => {
        if(options === null || !areArraysEqual(options, optionsSelector)){
            setOptions(optionsSelector);
        }
        
        if(optionsSelector && optionsSelector.length === 1){
            setOptionSelected(optionsSelector[0].value as string);
        }
    }, [optionsSelector])

    useEffect(() => {
        if(defaultValue && defaultValue !== ""){
            setOptionSelected(defaultValue);
        }
    }, [defaultValue])

    function markAsErrorCallback(){
        setError(true);
    }

    function renderSelector(){
        if(!options){
            return <Loader />
        }
        if(options.length === 0){
            if(canSelectNoOption){
                return null;
            }
            return <Typography variant="h4" gutterBottom><Translate id={`general.use_selector.missing_option.${key}`} /></Typography>
            
        }
        else if(options.length === 1){
            if(noReturnIfOnlyOne){
                return null;
            }
            return(
                <>
                    <Typography variant="h4" gutterBottom><Translate id={`general.use_selector.label.${key}`} />:</Typography>
                    <Typography variant="h4" gutterBottom >{options[0].label}</Typography>
                </>
            )
            //return null;
        }
        else{
            let optionsArray = options.sort((a, b) => a.label.localeCompare(b.label)).map((option) => {
                return <MenuItem value={option.value}>{option.label}</MenuItem>
            })
            console.log(key)
            console.log("optionsArray", optionsArray);
            if(canSelectNoOption){
                optionsArray = [<MenuItem value={"all"}><Translate id={`general.use_selector.all.${key}`} /></MenuItem>, ...optionsArray]
            }
            return(
                <Grid item xs={12} style={{paddingTop:'0.5rem', paddingBottom:'0.5rem'}}>
                    <FormControl variant="outlined"  style={{minWidth: 220}} error={error} >
                    <InputLabel id={key}><Translate id={`general.use_selector.select.${key}`} /></InputLabel>
                        <Select 
                            labelId={key}
                            id={key}
                            label={key}
                            value={optionSelected}
                            onChange={(event) => {
                                setError(false);
                                setOptionSelected(event.target.value as string)}}
                        >
                        { optionsArray }
                        </Select>
                    </FormControl>
                </Grid>
            )
        }
        
    }
    return { optionSelected, renderSelector, markAsErrorCallback}
}

export function useDeparmentsSelectorBis(defaultValue?:string, selectNoDepartment:boolean = false, uuidResearcher:string = "", noReturnIfOnlyOne:boolean = false){
    const { departments, loadingDepartments } = useDepartments(uuidResearcher);
    const [departmentSelected, setDepartmentSelected] = useState<IDepartment | null>(null);

    console.log("uuidresearcher", uuidResearcher);
    console.log("Departments", departments);
    const departmentOptions = useMemo(() => {
        return departments ? departments.map((department) => {
            return {
                label:department.name,
                value: department!.uuid as string
            }
        }) : [];
    }, [departments]);

    const { optionSelected, renderSelector, markAsErrorCallback} = useSelectLogic(departmentOptions, "department", defaultValue, selectNoDepartment, noReturnIfOnlyOne)

    useEffect(() =>{
        if(optionSelected){
            const findDepartment = departments?.find((department) => department.uuid === optionSelected);
            if(findDepartment){
                setDepartmentSelected(findDepartment);
            }
        }
        
    }, [optionSelected, departments])
    
    return {uuidDepartmentSelected:optionSelected, departments, departmentSelected, loadingDepartments,
            renderDepartmentSelector:renderSelector, 
            markAsErrorDepartmentCallback: markAsErrorCallback }
}

export function useDeparmentsSelector(selectNoDepartment:boolean = false, uuidResearchersDepartmentsOnly:string = "", noReturnIfOnlyOne:boolean = false, defaultValue?:string){
    const { departments } = useDepartments(uuidResearchersDepartmentsOnly);
    const [departmentSelected, setDepartmentSelected] = React.useState<string | null>(defaultValue ? defaultValue : null);
    const [errorDepartment, setErrorDepartment] = React.useState(false);

    useEffect(() => {
        if(departments && departments.length === 1 && !defaultValue){
            setDepartmentSelected(departments[0].uuid as string);
        }
    }, [departments])

    useEffect(() => {
        if(departmentSelected === ""){
            setDepartmentSelected(null);
        }
    }, [departmentSelected])

    function markAsErrorDepartmentCallback(){
        setErrorDepartment(true);
    }
    
    function renderDepartmentSelector(){
        if(!departments){
            return <Loader />
        }
        if(departments.length === 0){
            return null;
        }
        else if(departments.length === 1){
            if(noReturnIfOnlyOne){
                return null;
            }
            return(
                <>
                    <Typography variant="h4" gutterBottom>Department:</Typography>
                    <Typography variant="h4" gutterBottom >{departments[0].name}</Typography>
                </>
            )
            //return null;
        }
        else{
            let optionsArray = departments.sort((a, b) => a.name.localeCompare(b.name)).map((department) => {
                return <MenuItem value={department.uuid}>{department.name}</MenuItem>
            })
            if(selectNoDepartment){
                optionsArray = [<MenuItem value={"all"}><Translate id="hospital.departments.all_departments" /></MenuItem>, ...optionsArray]
            }
            return(
                <FormControl 
                    variant="outlined" 
                    fullWidth
                    error={errorDepartment}
                >
                    <InputLabel id="department">
                        <Translate id="pages.hospital.pharmacy.request.select_department" />
                    </InputLabel>
                    <Select 
                        labelId="department"
                        id="department"
                        label="department"
                        value={departmentSelected}
                        onChange={(event) => {
                            setErrorDepartment(false);
                            setDepartmentSelected(event.target.value as string)
                        }}
                    >
                        {optionsArray}
                    </Select>
                </FormControl>
            );
        }
        
    }
    return { departmentSelected, renderDepartmentSelector, markAsErrorDepartmentCallback, departments}
}

export function useResearchersSelector(defaultValue?:string, selectNoDepartment:boolean = false, noReturnIfOnlyOne:boolean = false){
    const { researchers, loadingDepartments } = useDepartments();
    const [researcherSelected, setResearcherSelected] = useState<IResearcher | null>(null);

    const researcherOptions = researchers ? researchers.map((researcher) => {
        return {
            label:researcherFullName(researcher),
            value: researcher!.uuid as string
        }
    }) : [];
    const { optionSelected, renderSelector, markAsErrorCallback} = useSelectLogic(researcherOptions, "researcher", defaultValue, selectNoDepartment, noReturnIfOnlyOne)
    
    useEffect(() =>{
        if(optionSelected){
            const findResearcher = researchers?.find((researcher) => researcher.uuid === optionSelected);
            if(findResearcher){
                setResearcherSelected(findResearcher);
            }
        }
        
    }, [optionSelected, researchers])
    
    return {uuidResearcherSelected:optionSelected, researcherSelected, researchers, loadingResearchers:loadingDepartments,
            renderResearcherSelector:renderSelector, 
            markAsErrorReseacherCallback: markAsErrorCallback }
}

export function useResearcherDepartmentSelector(defaultValueResearcher?:string, defaultValueDepartment?:string){
    
    const { researchers, loadingResearchers, renderResearcherSelector, uuidResearcherSelected, researcherSelected, markAsErrorReseacherCallback  } = useResearchersSelector(defaultValueResearcher);
    const { departments, loadingDepartments, renderDepartmentSelector, uuidDepartmentSelected, departmentSelected, markAsErrorDepartmentCallback } = useDeparmentsSelectorBis(defaultValueDepartment, false, uuidResearcherSelected ? uuidResearcherSelected : "", false);
    console.log(departments);
    function renderResearcherDepartmentSelector(){
        let selects = [renderResearcherSelector()];
        if(researcherSelected){
            selects = [...selects, renderDepartmentSelector()]

        }

        return selects;
    }
    return {researchers, renderResearcherDepartmentSelector, uuidResearcherSelected,
            departments,
            loadingResearcherOrDepartments:(loadingDepartments || loadingResearchers), 
            researcherSelected, uuidDepartmentSelected, departmentSelected,  
            markAsErrorReseacherCallback, markAsErrorDepartmentCallback}
}

const usePageVisibility = (onVisible: () => void) => {
    useEffect(() => {
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          onVisible();
        }
      };
  
      document.addEventListener('visibilitychange', handleVisibilityChange);
  
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }, [onVisible]);
  };
  
  export default usePageVisibility;

  export function useDeletePatient(uuidInvestigation: string, uuidPatient: string) {
    const [loadingDeletePatient, setLoadingDeletePatient] = useState(false);
    const [deletedPatient, setDeletedPatient] = useState(false);
    const dispatch = useDispatch();

    async function deletePatientService() {
        setLoadingDeletePatient(true);
        try {
            await dispatch(deletePatientAction(uuidInvestigation, uuidPatient));
            setDeletedPatient(true);
        } catch (error) {
            console.error('Error deleting patient:', error);
        } finally {
            setLoadingDeletePatient(false);
        }
    }
    
    return { loadingDeletePatient, deletedPatient, deletePatientService };
}

  export function usePatients(uuidInvestigation:string){
    const patients:IPatient[] = useSelector((state: any) => state.patients.data ? state.patients.data[uuidInvestigation] : null) ;
    const loadingPatients = useSelector((state: any) => state.patients.loading);
    const investigations = useSelector((state: any) => state.investigations);
    const dispatch = useDispatch();

    useEffect(() => {
        async function fetchPatients() {
            if (investigations.currentInvestigation?.uuid) {
                // Assuming you have a Redux action for fetching patients
                await dispatch(
                    fetchPatientsAction(investigations.currentInvestigation.uuid)
                );
            }
        }

        if (investigations.currentInvestigation) {
            fetchPatients();
        }
    }, [investigations.currentInvestigation]);

    return { patients, loadingPatients };
}

export function useCurrentInvestigation() {
    const currentInvestigation = useSelector((state: any) => state.investigations.currentInvestigation);
    
    const hasFunctionality = (functionality: string): boolean => {
        return currentInvestigation?.functionalities?.includes(functionality) || false;
    };
    
    const isBillingEnabled = (): boolean => {
        return hasFunctionality("BILLING");
    };
    
    const isHospitalizationEnabled = (): boolean => {
        return hasFunctionality("HOSPITALIZATION");
    };
    
    const isOutpatientsEnabled = (): boolean => {
        return hasFunctionality("OUTPATIENTS");
    };
    
    const isRequestsEnabled = (): boolean => {
        return hasFunctionality("REQUESTS");
    };
    
    const isSurgeryEnabled = (): boolean => {
        return hasFunctionality("SURGERY");
    };
    
    const isPharmacyCentralEnabled = (): boolean => {
        return hasFunctionality("PHARMACY_CENTRAL");
    };
    
    const isShoeShopEnabled = (): boolean => {
        return hasFunctionality("SHOE_SHOP");
    };
    
    return {
        currentInvestigation,
        functionalities: currentInvestigation?.functionalities || [],
        hasFunctionality,
        isBillingEnabled,
        isHospitalizationEnabled,
        isOutpatientsEnabled,
        isRequestsEnabled,
        isSurgeryEnabled,
        isPharmacyCentralEnabled,
        isShoeShopEnabled
    };
}

export function requestAppointmetToIcon(requestStatus:RequestStatus){
    let icon = null;
    switch (requestStatus) {
        case RequestStatus.COMPLETED:
            icon = <Done fontSize="small" />;
            break;
        case RequestStatus.ACCEPTED:
            icon = <Paid fontSize="small" />;
            break;
        case RequestStatus.PENDING_PAYMENT:
            icon = <MoneyOff fontSize="small" />;
            break;
        case RequestStatus.EXPIRED:
            icon = <EventBusy fontSize="small" />;
            break;
        case RequestStatus.CANCELED_BY_USER:
            icon = <EventBusy fontSize="small" />;
            break;
        default:
            return null;
    }
    return icon;
}

// Export PDF Configuration Hook
export { usePDFConfig } from './usePDFConfig';
